import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ArrowLeft, CheckCircle2, X, Loader2, AlertCircle, Search, Plus, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall, type Member } from '@/lib/api';

interface SessionInfo {
  id: string; class_id: string; starts_at: string; ends_at: string; capacity: number;
  class_name: string; class_color: string; coach_name: string | null;
}
interface Registration {
  id: string;
  status: 'registered' | 'attended' | 'no_show' | 'cancelled';
  member_id: string | null;
  visitor_name: string | null;
  member_name: string | null;
  member_email: string | null;
}
interface Suggested {
  id: string; name: string; email: string | null; last_at: string | null; recent_count: number;
}
interface DetailResp {
  session: SessionInfo;
  registrations: Registration[];
  suggested: Suggested[];
}

export default function SessionRollCall() {
  const { id } = useParams<{ id: string }>();
  const { getToken } = useAuth();
  const [data, setData] = useState<DetailResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Member[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const r = await apiCall<DetailResp>(`/v1/classes/sessions/${id}/detail`, { bearerToken: token });
      setData(r);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken, id]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!search) { setSearchResults([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const r = await apiCall<{ items: Member[] }>('/v1/members', {
          bearerToken: token,
          query: { search, status: 'active', per_page: 20 },
        });
        if (!cancelled) setSearchResults(r.items);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [search, getToken]);

  async function mark(memberId: string, status: 'attended' | 'no_show') {
    if (!id) return;
    setPending(memberId);
    try {
      const token = await getToken();
      await apiCall(`/v1/classes/sessions/${id}/rollcall`, {
        method: 'POST', bearerToken: token, body: { member_id: memberId, status },
      });
      await load();
      setSearch('');
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setPending(null); }
  }

  if (loading && !data) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (err && !data) return <Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-3 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {err}</CardContent></Card>;
  if (!data) return null;

  const start = new Date(data.session.starts_at);
  const registeredCount = data.registrations.filter((r) => r.status !== 'cancelled').length;
  const attendedCount = data.registrations.filter((r) => r.status === 'attended').length;
  const noShowCount = data.registrations.filter((r) => r.status === 'no_show').length;
  const enrolledIds = new Set(data.registrations.map((r) => r.member_id).filter(Boolean) as string[]);
  const visibleSearch = searchResults.filter((m) => !enrolledIds.has(m.id));

  return (
    <div className="space-y-4 max-w-4xl">
      <Button variant="ghost" size="sm" asChild><Link to="/dashboard/schedule"><ArrowLeft className="mr-1 h-4 w-4" /> Schedule</Link></Button>

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">{data.session.class_name}</h1>
        <p className="text-sm text-muted-foreground">
          {start.toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          {data.session.coach_name && ` · ${data.session.coach_name}`}
          {' · '}capacity {data.session.capacity}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="py-3"><div className="text-2xl font-bold">{registeredCount}</div><div className="text-xs text-muted-foreground">registered</div></CardContent></Card>
        <Card><CardContent className="py-3"><div className="text-2xl font-bold text-emerald-700">{attendedCount}</div><div className="text-xs text-muted-foreground">attended</div></CardContent></Card>
        <Card><CardContent className="py-3"><div className="text-2xl font-bold text-muted-foreground">{noShowCount}</div><div className="text-xs text-muted-foreground">no-show</div></CardContent></Card>
      </div>

      {/* Search to add anyone */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search any active member to add to this class…"
              className="w-full pl-8 pr-3 py-2 border rounded text-sm"
            />
          </div>
          {visibleSearch.length > 0 && (
            <ul className="divide-y border rounded max-h-64 overflow-y-auto">
              {visibleSearch.map((m) => (
                <li key={m.id} className="px-3 py-2 text-sm flex items-center justify-between hover:bg-muted/40">
                  <span>{m.name}{m.email && <span className="text-xs text-muted-foreground ml-2">{m.email}</span>}</span>
                  <Button size="sm" onClick={() => void mark(m.id, 'attended')} disabled={pending === m.id} className="h-7">
                    {pending === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    Attended
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Current roster */}
      {data.registrations.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-2 text-xs text-muted-foreground border-b font-medium flex items-center gap-2">
              <Users className="h-3.5 w-3.5" /> Roster ({registeredCount})
            </div>
            <ul className="divide-y">
              {data.registrations.map((r) => (
                <li key={r.id} className="px-4 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{r.member_name ?? r.visitor_name ?? '(unknown)'}</div>
                    {r.member_email && <div className="text-xs text-muted-foreground">{r.member_email}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={r.status === 'attended' ? 'default' : r.status === 'no_show' ? 'destructive' : r.status === 'cancelled' ? 'outline' : 'secondary'}>
                      {r.status}
                    </Badge>
                    {r.member_id && r.status !== 'attended' && (
                      <Button size="sm" variant="outline" className="h-7" onClick={() => void mark(r.member_id!, 'attended')} disabled={pending === r.member_id}>
                        <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-600" /> Attended
                      </Button>
                    )}
                    {r.member_id && r.status !== 'no_show' && (
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => void mark(r.member_id!, 'no_show')} disabled={pending === r.member_id}>
                        <X className="h-3 w-3" /> No-show
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Suggested — frequent attendees */}
      {data.suggested.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-2 text-xs text-muted-foreground border-b font-medium">
              Frequent attendees — tap to add
            </div>
            <ul className="divide-y">
              {data.suggested.map((s) => (
                <li key={s.id} className="px-4 py-2 flex items-center justify-between hover:bg-muted/40">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.recent_count} {s.recent_count === 1 ? 'visit' : 'visits'} in 60d</div>
                  </div>
                  <Button size="sm" onClick={() => void mark(s.id, 'attended')} disabled={pending === s.id} className="h-7">
                    {pending === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    Attended
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
